/**
 * 기능 : 브라우저 마이크 접근 권한 상태 조회 및 요청 유틸
 *
 * 실시간 방송은 브라우저 오디오를 서버 릴레이로 전송하는 구조이므로
 * 음성 / 음원 / TTS 방송 모두 마이크 스트림(mixBus)이 있어야 장비로 소리가 전달된다.
 * 따라서 방송 시작 전 권한 확인은 모든 탭의 공통 선행 조건이다.
 */

/** granted: 승인됨, denied: 차단됨, prompt: 아직 요청 전, unsupported: 브라우저/보안컨텍스트 미지원 */
export type MicPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

/** getUserMedia 사용 가능 여부 (http 등 비보안 컨텍스트에서는 mediaDevices 자체가 없음) */
const isMediaDevicesSupported = (): boolean => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

/**
 * 현재 마이크 권한 상태를 반환한다.
 * Permissions API를 지원하지 않는 브라우저(Safari 등)는 상태 확인이 불가능하므로
 * 사용자가 직접 요청할 수 있도록 'prompt'로 처리한다.
 */
export const getMicPermissionState = async (): Promise<MicPermissionState> => {
  if (!isMediaDevicesSupported()) return 'unsupported';
  if (!navigator.permissions?.query) return 'prompt';

  try {
    const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return status.state;
  } catch {
    return 'prompt';
  }
};

/**
 * 브라우저 권한 요청 창을 띄우고 승인 여부를 반환한다.
 * 승인된 스트림은 권한 확인 용도로만 사용하므로 즉시 해제한다.
 */
export const requestMicPermission = async (): Promise<boolean> => {
  if (!isMediaDevicesSupported()) return false;

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (error) {
    console.error('[micPermission] 마이크 권한 요청 실패', error);
    return false;
  }
};
